// skipcq: JS-W1028
import { useCallback, useId, useState } from 'react';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  formatIngestionRunEnumLabel,
  formatIngestionRunTimestamp,
  isExtractionRun,
} from '../../utils/brewery/beerIngestionRunFilters';
import {
  INGESTION_STAGE_STATE_COMPLETED,
  INGESTION_STAGE_STATE_CURRENT,
  INGESTION_STAGE_STATE_ERROR,
  resolveIngestionRunStageStates,
  shouldShowIngestionRunStageStepper,
} from '../../utils/brewery/beerIngestionStages';
import { importResultsFromIngestionRun } from '../../utils/brewery/beerIngestionReview';
import BeerIngestionComparisonPanel from './BeerIngestionComparisonPanel';
import BeerIngestionSnapshotPanel from './BeerIngestionSnapshotPanel';
import ImportSummaryView from './findBeer/ImportSummaryView';
import { BreweryNameCell } from '../beerIngestion/beerIngestionPanels';

function formatMetadataDisplay(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

const RUN_SUMMARY_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)',
  },
  columnGap: 2,
  rowGap: 1,
  mb: 1.5,
};

const RUN_SUMMARY_CELL_SX = { mb: 0, minWidth: 0 };

const STAGE_CHIPS_SECTION_SX = {
  mt: 0.5,
  mb: 1.5,
};

const STAGE_CHIPS_ROW_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  flexWrap: { xs: 'wrap', md: 'nowrap' },
};

const COMPLETED_STAGE_CHIP_SX = {
  '& .MuiChip-icon': {
    color: 'success.main',
  },
};

function CompletedStageCheckIcon() {
  return <CheckCircleIcon fontSize="small" data-testid="completed-stage-check" />;
}

function RunMetadataRow({ label, value, sx }) {
  return (
    <Box sx={{ mb: 1, minWidth: 0, ...sx }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
        {formatMetadataDisplay(value)}
      </Typography>
    </Box>
  );
}

RunMetadataRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
  sx: PropTypes.object,
};

function formatBeerCountChipLabel(run) {
  if (isExtractionRun(run)) {
    const count = run.beersExtractedCount ?? 0;
    return `Beers extracted: ${count}`;
  }
  return `Added ${run.beersAddedCount || 0} · Updated ${run.beersUpdatedCount || 0} · Errored ${run.beersErroredCount || 0}`;
}

function RunKindStatusChips({ run }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      <Chip size="small" label={formatIngestionRunEnumLabel(run.runKind)} />
      <Chip size="small" label={formatIngestionRunEnumLabel(run.status)} />
      <Chip size="small" label={formatIngestionRunEnumLabel(run.trigger)} />
      <Chip size="small" label={formatBeerCountChipLabel(run)} />
      {run.costCredits != null ? (
        <Chip size="small" label={`Cost credits: ${run.costCredits}`} />
      ) : null}
    </Box>
  );
}

RunKindStatusChips.propTypes = {
  run: PropTypes.object.isRequired,
};

function RunSourceValue({ sourceUrl }) {
  if (!sourceUrl) {
    return '—';
  }
  return (
    <Link href={sourceUrl} target="_blank" rel="noopener noreferrer">
      {sourceUrl}
    </Link>
  );
}

RunSourceValue.propTypes = {
  sourceUrl: PropTypes.string,
};

function hasRunTechnicalIds(run) {
  return Boolean(run.extractorVersion || run.snapshotHash || run.checkId || run.extractId);
}

function RunTechnicalIdsContent({ run }) {
  return (
    <Box sx={{ p: 2, minWidth: 280, maxWidth: 420, '& > :last-child': { mb: 0 } }}>
      {run.extractorVersion ? (
        <RunMetadataRow label="Extractor version" value={run.extractorVersion} />
      ) : null}
      {run.snapshotHash ? (
        <RunMetadataRow label="Snapshot hash" value={run.snapshotHash} />
      ) : null}
      {run.checkId ? <RunMetadataRow label="Check id" value={run.checkId} /> : null}
      {run.extractId ? <RunMetadataRow label="Extract id" value={run.extractId} /> : null}
    </Box>
  );
}

RunTechnicalIdsContent.propTypes = {
  run: PropTypes.object.isRequired,
};

export function RunTechnicalIdsPopover({ run }) {
  const popoverId = useId();
  const [anchorElement, setAnchorElement] = useState(null);
  const open = Boolean(anchorElement);

  const handleOpen = useCallback((event) => {
    setAnchorElement(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorElement(null);
  }, []);

  if (!hasRunTechnicalIds(run)) {
    return null;
  }

  return (
    <>
      <Tooltip title="Technical identifiers">
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="Run keys"
          aria-expanded={open}
          aria-describedby={open ? popoverId : undefined}
          sx={{ color: 'text.secondary' }}
        >
          <VpnKeyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorElement}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <RunTechnicalIdsContent run={run} />
      </Popover>
    </>
  );
}

RunTechnicalIdsPopover.propTypes = {
  run: PropTypes.object.isRequired,
};

function RunSummaryRow({ run, breweryId, breweryName, openBreweryInNewTab = false }) {
  const completedDisplay = run.completedAt
    ? formatIngestionRunTimestamp(run.completedAt)
    : 'In progress';
  const resolvedBreweryId = breweryId || run.breweryId;
  const breweryValue = resolvedBreweryId
    ? (
      <BreweryNameCell
        name={breweryName}
        breweryId={resolvedBreweryId}
        openInNewTab={openBreweryInNewTab}
      />
    )
    : formatMetadataDisplay(breweryName);

  return (
    <Box role="group" aria-label="Run summary" sx={RUN_SUMMARY_ROW_SX}>
      <RunMetadataRow label="Brewery" value={breweryValue} sx={RUN_SUMMARY_CELL_SX} />
      <RunMetadataRow
        label="Source"
        value={<RunSourceValue sourceUrl={run.sourceUrl} />}
        sx={RUN_SUMMARY_CELL_SX}
      />
      <RunMetadataRow
        label="Started"
        value={formatIngestionRunTimestamp(run.startedAt)}
        sx={RUN_SUMMARY_CELL_SX}
      />
      <RunMetadataRow label="Completed" value={completedDisplay} sx={RUN_SUMMARY_CELL_SX} />
    </Box>
  );
}

RunSummaryRow.propTypes = {
  run: PropTypes.object.isRequired,
  breweryId: PropTypes.string,
  breweryName: PropTypes.string,
  openBreweryInNewTab: PropTypes.bool,
};

function getStageChipAppearance(state) {
  if (state === INGESTION_STAGE_STATE_ERROR) {
    return { color: 'error', variant: 'filled' };
  }
  if (state === INGESTION_STAGE_STATE_CURRENT) {
    return { color: 'primary', variant: 'filled' };
  }
  if (state === INGESTION_STAGE_STATE_COMPLETED) {
    return {
      color: 'default',
      variant: 'outlined',
      showCheck: true,
      sx: COMPLETED_STAGE_CHIP_SX,
    };
  }
  return { color: 'default', variant: 'outlined' };
}

function RunStageChip({ item }) {
  const appearance = getStageChipAppearance(item.state);
  return (
    <Chip
      size="small"
      label={item.label}
      icon={appearance.showCheck ? <CompletedStageCheckIcon /> : undefined}
      color={appearance.color}
      variant={appearance.variant}
      aria-label={item.ariaLabel}
      sx={appearance.sx}
    />
  );
}

RunStageChip.propTypes = {
  item: PropTypes.shape({
    stage: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string.isRequired,
  }).isRequired,
};

function renderRunStageChip(item) {
  return <RunStageChip key={item.stage} item={item} />;
}

function RunStageChips({ run }) {
  if (!shouldShowIngestionRunStageStepper(run)) {
    return null;
  }
  const stageItems = resolveIngestionRunStageStates(run);
  return (
    <Box sx={STAGE_CHIPS_SECTION_SX}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
        Stages
      </Typography>
      <Box role="group" aria-label="Ingestion stages" sx={STAGE_CHIPS_ROW_SX}>
        {stageItems.map(renderRunStageChip)}
      </Box>
    </Box>
  );
}

RunStageChips.propTypes = {
  run: PropTypes.object.isRequired,
};

function ImportRunResults({ run }) {
  const importResults = importResultsFromIngestionRun(run);
  if (!importResults) {
    return null;
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Import results
      </Typography>
      <ImportSummaryView importResults={importResults} hideEmptyAlert />
    </Box>
  );
}

ImportRunResults.propTypes = {
  run: PropTypes.object.isRequired,
};

export default function BeerIngestionRunDetailBody({
  run,
  snapshot,
  comparison,
  review,
  breweryId,
  breweryName,
  previousRun,
  onPreviousRunClick,
  onReviewUpdated,
  onApplySuccess,
  openBreweryInNewTab = false,
}) {
  const showFailure = run.status === 'failure' && run.errorMessage;
  return (
    <>
      <RunKindStatusChips run={run} />
      <RunSummaryRow
        run={run}
        breweryId={breweryId}
        breweryName={breweryName}
        openBreweryInNewTab={openBreweryInNewTab}
      />
      <RunStageChips run={run} />
      {previousRun ? (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={onPreviousRunClick}>
            View previous successful run
          </Button>
        </Box>
      ) : null}
      {showFailure ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {run.errorMessage}
        </Alert>
      ) : null}
      <ImportRunResults run={run} />
      {(run.hasComparison || comparison || run.comparisonErrorMessage) && snapshot ? (
        <BeerIngestionComparisonPanel
          run={run}
          snapshot={snapshot}
          comparison={comparison}
          review={review}
          breweryId={breweryId}
          onReviewUpdated={onReviewUpdated}
          onApplySuccess={onApplySuccess}
        />
      ) : (
        <BeerIngestionSnapshotPanel
          run={run}
          snapshot={snapshot}
          breweryId={breweryId}
        />
      )}
    </>
  );
}

BeerIngestionRunDetailBody.propTypes = {
  run: PropTypes.object.isRequired,
  snapshot: PropTypes.object,
  comparison: PropTypes.object,
  review: PropTypes.object,
  breweryId: PropTypes.string,
  breweryName: PropTypes.string,
  previousRun: PropTypes.object,
  onPreviousRunClick: PropTypes.func,
  onReviewUpdated: PropTypes.func,
  onApplySuccess: PropTypes.func,
  openBreweryInNewTab: PropTypes.bool,
};
